import { useMemo } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

/**
 * Renders RADaR's reconstructed `meta_text`, parsing the inline tokens:
 *   [[RESTORED:org=X|words=N]]…[[/RESTORED]]   — .pd-mark-restored + tooltip
 *   [[UNRESOLVED:type=X]]…[[/UNRESOLVED]]      — .pd-mark-unresolved + tooltip
 *   [[DISCREPANCY:primary=A|secondary=B]]      — .pd-mark-discrepancy + tooltip
 * Anything outside tokens is plain primary-source content.
 */
function parseAttrs(s) {
  const out = {};
  if (!s) return out;
  s.replace(/^[:|]/, '').split('|').forEach((pair) => {
    const idx = pair.indexOf('=');
    if (idx > -1) out[pair.slice(0, idx).trim()] = pair.slice(idx + 1).trim();
  });
  return out;
}

function tokenize(text) {
  if (!text) return [];
  const parts = [];
  let last = 0;
  // Use a simpler approach: handle paired tokens manually.
  const re = /\[\[(RESTORED|UNRESOLVED|DISCREPANCY)([^\]]*)\]\]([\s\S]*?)\[\[\/\1\]\]/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ kind: 'text', body: text.slice(last, m.index) });
    parts.push({
      kind: m[1].toLowerCase(),
      attrs: parseAttrs(m[2]),
      body: m[3],
    });
    last = re.lastIndex;
  }
  if (last < text.length) parts.push({ kind: 'text', body: text.slice(last) });
  return parts;
}

/** Shared reading-body classes: 18px / 1.85, 68ch measure. */
export const READING_BODY_CLASS = 'text-[18px] leading-[1.85] max-w-[68ch] text-ink whitespace-pre-wrap';

/**
 * Legend for the four inline marks. Sits under any reading body.
 */
export const MarkLegend = () => (
  <dl
    className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 pt-5 mt-6 border-t border-rule text-[15px] text-ink-55"
    data-testid="mark-legend"
  >
    {[
      ['pd-mark-redacted', 'Redacted', 'as it appears on the page.'],
      ['pd-mark-restored', 'Restored', 'recovered from another copy.'],
      ['pd-mark-discrepancy', 'Discrepancy', 'copies disagree, both shown.'],
      ['pd-mark-unresolved', 'Unresolved', 'still missing, with the challenge route.'],
    ].map(([cls, name, desc]) => (
      <div key={cls} className="flex items-center gap-3">
        <dt className="flex-shrink-0">
          <span className={`${cls} inline-block w-12 h-[14px] align-middle`} aria-hidden="true" />
          <span className="sr-only">{name}</span>
        </dt>
        <dd>
          <span className="font-semibold text-ink">{name}</span> — {desc}
        </dd>
      </div>
    ))}
  </dl>
);

export const RadarMetaView = ({ metaText, summary }) => {
  const parts = useMemo(() => tokenize(metaText || ''), [metaText]);

  if (!metaText) {
    return (
      <p className="text-ink-55">No reconstructed text yet.</p>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4">
        {summary && (
          <div className="flex flex-wrap gap-2 text-[13px] font-medium" data-testid="radar-summary">
            {summary.total_words_restored > 0 && (
              <span className="rounded-full bg-brand-wash text-brand-ink px-[11px] py-1">
                {summary.total_words_restored} words restored
              </span>
            )}
            {summary.unresolved > 0 && (
              <span className="rounded-full bg-breach-wash text-breach-ink px-[11px] py-1">
                {summary.unresolved} unresolved
              </span>
            )}
            {summary.discrepancies > 0 && (
              <span className="rounded-full bg-amber-wash text-amber-ink px-[11px] py-1">
                {summary.discrepancies} discrepanc{summary.discrepancies === 1 ? 'y' : 'ies'}
              </span>
            )}
            {summary.originator_withheld_count > 0 && (
              <span className="rounded-full bg-breach-wash text-breach-ink px-[11px] py-1 font-semibold">
                {summary.originator_withheld_count} withheld by the author
              </span>
            )}
          </div>
        )}
        <div className={READING_BODY_CLASS} data-testid="radar-meta-text">
          {parts.map((p, i) => {
            if (p.kind === 'text') return <span key={i}>{p.body}</span>;
            if (p.kind === 'restored') {
              const org = p.attrs.org || 'another source';
              const words = p.attrs.words || '?';
              return (
                <Tooltip key={i}>
                  <TooltipTrigger asChild>
                    <span className="pd-mark-restored cursor-help" data-testid={`radar-restored-${i}`}>
                      {p.body}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    Restored from {org} · {words} words
                  </TooltipContent>
                </Tooltip>
              );
            }
            if (p.kind === 'unresolved') {
              const type = p.attrs.type || 'redacted';
              return (
                <Tooltip key={i}>
                  <TooltipTrigger asChild>
                    <span className="pd-mark-unresolved cursor-help" data-testid={`radar-unresolved-${i}`}>
                      {p.body}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    Still missing · {type}. See comparison findings for the challenge route.
                  </TooltipContent>
                </Tooltip>
              );
            }
            if (p.kind === 'discrepancy') {
              return (
                <Tooltip key={i}>
                  <TooltipTrigger asChild>
                    <span className="pd-mark-discrepancy cursor-help" data-testid={`radar-discrepancy-${i}`}>
                      {p.body}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs max-w-xs">
                    The copies disagree.
                    {p.attrs.primary && <div className="mt-1"><b>One copy:</b> {p.attrs.primary}</div>}
                    {p.attrs.secondary && <div><b>The other:</b> {p.attrs.secondary}</div>}
                  </TooltipContent>
                </Tooltip>
              );
            }
            return null;
          })}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default RadarMetaView;
